/**
 * Outlook / Word paste: HTML has empty `cid:` / `file://` / Outlook-blob
 * `<img>` slots. Photo bytes arrive as separate Files. Map files onto those
 * slots in document order so they are not appended at the end.
 */

const UNRESOLVED_IMG_SRC = /^(cid:|file:|blob:https:\/\/outlook\.)/i;
const RESOLVED_IMG_SRC =
  /^(?:https?:|data:|blob:|sitescope-attachment|javascript:|vbscript:)/i;

export function isUnresolvedOutlookImageSrc(src: string): boolean {
  const trimmed = src.trim();
  if (trimmed.length === 0 || UNRESOLVED_IMG_SRC.test(trimmed)) {
    return true;
  }
  // Word writes the picture as src="clip_image001.png" (no scheme).
  return (
    !RESOLVED_IMG_SRC.test(trimmed) && !/^[a-z][a-z0-9+.-]*:/i.test(trimmed)
  );
}

function imgSrc(tag: string): string {
  const padded = ` ${tag}`;
  const quoted = /(?:\s|^)src\s*=\s*(["'])([\s\S]*?)\1/i.exec(padded);
  if (quoted?.[2] != null) {
    return quoted[2].trim();
  }
  const unquoted = /(?:\s|^)src\s*=\s*([^\s>]+)/i.exec(padded);
  return (unquoted?.[1] ?? '').replace(/["']/g, '').trim();
}

function imgOriginalSrc(tag: string): string | null {
  const match = /\boriginalsrc=(["'])(.*?)\1/i.exec(tag);
  return match?.[2] != null ? match[2].trim() : null;
}

function imgAlt(tag: string): string {
  const match = /\balt\s*=\s*(["'])([\s\S]*?)\1/i.exec(tag);
  return match?.[2]?.trim() ?? '';
}

function replaceImgSrc(tag: string, preview: string): string {
  const quoted = /(\ssrc\s*=\s*)(["'])[\s\S]*?\2/i;
  if (quoted.test(tag)) {
    return tag.replace(quoted, `$1"${preview}"`);
  }
  const unquoted = /(\ssrc\s*=\s*)([^\s>]+)/i;
  if (unquoted.test(tag)) {
    return tag.replace(unquoted, `$1"${preview}"`);
  }
  return tag.replace(/<img\b/i, `<img src="${preview}"`);
}

const HOSTED_IMAGE_ATTRS = [
  'originalsrc',
  'data-src',
  'data-originalsrc',
  'data-mce-src',
];

/** cid/file/Outlook-blob src with an https address on the same tag. Keep that URL. */
export function rewriteUnresolvedImageSrcsToHostedUrls(html: string): string {
  if (!html || !/<img\b/i.test(html)) {
    return html;
  }
  return html.replace(/<img\b[^>]*>/gi, tag => {
    const src = imgSrc(tag);
    if (!isUnresolvedOutlookImageSrc(src)) {
      return tag;
    }
    const hosted = hostedImageUrl(tag);
    if (!hosted) {
      return tag;
    }
    return replaceImgSrc(tag, hosted);
  });
}

function hostedImageUrl(tag: string): string | null {
  for (const name of HOSTED_IMAGE_ATTRS) {
    const match = new RegExp(`\\b${name}\\s*=\\s*(["'])([^"']+)\\1`, 'i').exec(
      tag,
    );
    const value = match?.[2]?.trim() ?? '';
    if (/^https?:\/\//i.test(value)) {
      return value;
    }
  }
  return null;
}

export function stitchClipboardImagesIntoHtml(
  html: string,
  previewSrcs: string[],
): { html: string; used: number } {
  if (!html.trim() || previewSrcs.length === 0) {
    return { html, used: 0 };
  }
  let used = 0;
  const next = html.replace(/<img\b[^>]*>/gi, tag => {
    const src = imgSrc(tag);
    const original = imgOriginalSrc(tag);
    const unresolved =
      isUnresolvedOutlookImageSrc(src) ||
      (original !== null && isUnresolvedOutlookImageSrc(original));
    if (!unresolved) {
      return tag;
    }
    if (used >= previewSrcs.length) {
      return tag;
    }
    const preview = previewSrcs[used];
    used += 1;
    return replaceImgSrc(tag, preview);
  });
  return { html: next, used };
}

/**
 * Chrome's "Copy image" double: the clipboard carries an `<img src="https://…">`
 * AND the picture's bytes as a file. When the HTML is a picture and nothing
 * else, the file IS the picture, so the `<img>` goes and the file is inserted
 * through the ordinary leftover path (keeping its name; `alts` carries the
 * dropped tags' alt text so the insert can keep that too). Only tags whose
 * bytes we do not already hold (`haveBytes` false) are candidates, and only
 * when every candidate has a file: with more candidates than files we cannot
 * say which picture the file is, so nothing is dropped. ONE walk over the
 * tags (correspondence.md's chain rule): the match gives index, length, src
 * and alt, and the string is spliced once.
 */
export function dropImgsCoveredByClipboardFiles(
  html: string,
  filesLeft: number,
  haveBytes: (src: string) => boolean,
): { html: string; dropped: number; alts: string[] } {
  if (!html.trim() || filesLeft <= 0) {
    return { html, dropped: 0, alts: [] };
  }
  const candidates: Array<{ start: number; end: number; alt: string }> = [];
  for (const match of html.matchAll(/<img\b[^>]*>/gi)) {
    const tag = match[0] ?? '';
    if (haveBytes(imgSrc(tag))) {
      continue;
    }
    candidates.push({
      start: match.index ?? 0,
      end: (match.index ?? 0) + tag.length,
      alt: imgAlt(tag),
    });
  }
  if (candidates.length === 0 || candidates.length > filesLeft) {
    return { html, dropped: 0, alts: [] };
  }
  let next = '';
  let cursor = 0;
  for (const candidate of candidates) {
    next += html.slice(cursor, candidate.start);
    cursor = candidate.end;
  }
  next += html.slice(cursor);
  return {
    html: next,
    dropped: candidates.length,
    alts: candidates.map(candidate => candidate.alt),
  };
}

export type PasteHarnessSnapshot = {
  clipboardTypes: string[];
  htmlLength: number;
  rtfLength: number;
  imgSrcSchemes: string[];
  imageFiles: Array<{ name: string; type: string; size: number }>;
  stitched: number;
  leftover: number;
};

function classifyClipboardImgSrc(src: string): string {
  const trimmed = (src ?? '').trim();
  if (/^cid:/i.test(trimmed)) {
    return 'cid';
  }
  if (/^data:/i.test(trimmed)) {
    return 'data';
  }
  if (/^blob:/i.test(trimmed)) {
    return 'blob';
  }
  if (/^https?:/i.test(trimmed)) {
    return 'https';
  }
  if (/^file:/i.test(trimmed)) {
    return 'file';
  }
  return trimmed.slice(0, 24) || 'empty';
}

export function clipboardImgSrcSchemes(html: string): string[] {
  try {
    if (!html) {
      return [];
    }
    // `Array.from`, never `[...iterable]`: the babel-loader build runs the
    // spread transform loose (webpack.config.js), which compiles
    // `[...iterable]` to `[].concat(iterable)` and printed `[object Set]`
    // here. The hazard is REPO-WIDE, not this file's: `web-build/main.js`
    // holds `[].concat(new Set(...))` for other sites too (deferred-work.md
    // carries the list). Evidence is a grep of the built bundle; Jest runs
    // the RN preset without the collapse and cannot see it.
    return Array.from(html.matchAll(/<img\b[^>]*>/gi)).map(match => {
      const tag = match[0] ?? '';
      const fromSrc = classifyClipboardImgSrc(imgSrc(tag));
      if (fromSrc !== 'empty') {
        return fromSrc;
      }
      const original = imgOriginalSrc(tag);
      return original ? classifyClipboardImgSrc(original) : 'empty';
    });
  } catch {
    return ['error'];
  }
}

export function summarizeImgSrcSchemes(schemes: string[]): string {
  if (schemes.length === 0) {
    return 'none';
  }
  const counts = new Map<string, number>();
  for (const scheme of schemes) {
    counts.set(scheme, (counts.get(scheme) ?? 0) + 1);
  }
  return Array.from(counts.entries())
    .map(([scheme, count]) => (count > 1 ? `${scheme}×${count}` : scheme))
    .join(', ');
}

export function clipboardFlavorTypes(
  clipboard: DataTransfer | null | undefined,
): string[] {
  if (!clipboard) {
    return [];
  }
  const listed = Array.from(clipboard.types ?? []).filter(
    (entry): entry is string => typeof entry === 'string' && entry.length > 0,
  );
  const inferred: string[] = [];
  try {
    if ((clipboard.getData('text/html') ?? '').length > 0) {
      inferred.push('text/html');
    }
    if ((clipboard.getData('text/plain') ?? '').length > 0) {
      inferred.push('text/plain');
    }
    if ((clipboard.getData('text/rtf') ?? '').length > 0) {
      inferred.push('text/rtf');
    }
  } catch {
    // Chromium throws for some flavor reads.
  }
  if (clipboard.files && clipboard.files.length > 0) {
    inferred.push('Files');
  }
  return Array.from(new Set(listed.concat(inferred)));
}

export function isLocalPasteHarnessHost(hostname: string): boolean {
  return hostname === 'localhost' || hostname === '127.0.0.1';
}
